<script>
import { defineComponent, computed, onMounted, ref } from 'vue'
import { userFeedbackApi } from '@/services/userFeedbackApi'

/**
 * Product feedback control (PD-1289).
 *
 * A one-line "was this useful?" prompt that any feature can drop in. How it
 * presents itself is configuration, not code (PD-1289b): the caller names a
 * `feedbackType` and everything else comes from that feature's row in
 * `user_feedback_type`, so a prompt can be reworded, switched between thumbs and
 * stars, or made to ask for a comment without a deploy.
 *
 * `rating_type` picks the style:
 *   · boolean — thumbs up / down, stored as rating 1 / 0
 *   · stars   — a 1..`star_count` scale, stored as the star number
 * `capture_comment` decides which ratings open a comment box: all of them, none,
 * or a specific list — so a feature can ask "what went wrong?" on a thumbs-down
 * or a one/two-star score without slowing down the happy path. A rating that
 * doesn't open the box posts the moment it's clicked (one tap, which is the
 * point of the thumbs variant); one that does is only sent on confirm.
 *
 * No row, nothing rendered — and deliberately no built-in defaults to fall back
 * on. `user_feedback.feedback_type` keys to that table, so without the row a
 * rating could not be stored anyway: a prompt drawn from hardcoded defaults
 * would be one whose submit is guaranteed to fail.
 *
 * The context props map 1:1 onto the nullable columns of `user_feedback` — a
 * feature pins whichever it has, so the rating can be traced back to the thing
 * being rated.
 *
 * Feedback is advisory telemetry, so a failed save never interrupts the user:
 * it shows a quiet retry line and the host flow carries on regardless.
 */
export default defineComponent({
  name: 'cUserFeedback',
  props: {
    /**
     * Feature being rated. The only thing a caller chooses: it keys the
     * `user_feedback_type` row that configures this control, and is stored on
     * the feedback itself.
     */
    feedbackType: { type: String, required: true },

    // --- context (nullable `user_feedback` columns) ---
    docId: { type: [Number, String], default: null },
    documentLfEvaluationId: { type: [Number, String], default: null },
    fileId: { type: [Number, String], default: null },
    promptChainId: { type: [Number, String], default: null },
  },
  emits: ['submitted', 'error'],
  setup(props, { emit }) {
    const rating = ref(null)
    const hoverRating = ref(null)
    const comment = ref('')
    const saving = ref(false)
    const saved = ref(false)
    const error = ref(null)

    // This feature's `user_feedback_type` row. Null until it arrives, and null
    // for good if the lookup fails or the feature has no row — either way the
    // control stays invisible rather than offering a rating it can't store.
    const settings = ref(null)

    onMounted(async () => {
      try {
        settings.value = await userFeedbackApi.getSettings(props.feedbackType)
      } catch (err) {
        // Feedback is never the point of the page, so a settings call that
        // didn't come back is worth no more than silence.
        settings.value = null
      }
    })

    const labelText = computed(() => settings.value?.label ?? '')
    const placeholderText = computed(() => settings.value?.comment_placeholder ?? '')
    const thanksText = computed(() => settings.value?.thanks_label ?? '')
    // NULL here is a real answer — don't ask for a comment.
    const captureComment = computed(() => settings.value?.capture_comment ?? false)

    const isStars = computed(() => settings.value?.rating_type === 'stars')
    // Clamped rather than trusted: star_count is a plain TINYINT, and a stray 0
    // or 9 would leave the control unusable or record a rating the service
    // rejects (MAX_RATING).
    const starCount = computed(() => {
      const configured = Math.trunc(Number(settings.value?.star_count))
      return Number.isFinite(configured) ? Math.min(Math.max(configured, 2), 5) : 5
    })
    const stars = computed(() => Array.from({ length: starCount.value }, (_, i) => i + 1))

    // Does this particular rating warrant a comment? An array narrows the ask to
    // the listed values (Number-coerced, so ["0"] from a JSON column still
    // matches a thumbs-down); a plain boolean applies to every rating.
    function wantsComment(value) {
      if (Array.isArray(captureComment.value))
        return captureComment.value.some((v) => Number(v) === value)
      return captureComment.value === true
    }

    // The comment step only exists once something has been rated — there's
    // nothing to comment on before that.
    const commentOpen = computed(
      () => rating.value !== null && !saved.value && wantsComment(rating.value)
    )

    // Fill a star when it's at or below the hovered value (preview) or, with
    // nothing hovered, at or below the chosen one.
    const starFilled = (n) => n <= (hoverRating.value ?? rating.value ?? 0)

    function select(value) {
      if (saving.value || saved.value) return
      error.value = null
      rating.value = value
      // Nothing to gather for this rating: the click IS the submission.
      if (!wantsComment(value)) submit()
    }

    async function submit() {
      if (rating.value === null || saving.value) return
      saving.value = true
      error.value = null

      const payload = {
        feedback_type: props.feedbackType,
        rating: rating.value,
        comment: comment.value.trim() || null,
        doc_id: props.docId ?? null,
        document_lf_evaluation_id: props.documentLfEvaluationId ?? null,
        file_id: props.fileId ?? null,
        prompt_chain_id: props.promptChainId ?? null,
      }

      try {
        const resp = await userFeedbackApi.addFeedback(payload)
        // The service answers with a message rather than a status code when it
        // rejects the payload (unknown type, out-of-range rating), so treat a
        // missing id as a failure rather than reporting a false thank-you.
        if (!resp.data || !resp.data.Data || !resp.data.Data.user_feedback_id)
          throw new Error(resp.data?.Message || 'Feedback was not saved')
        saved.value = true
        emit('submitted', { ...payload, user_feedback_id: resp.data.Data.user_feedback_id })
      } catch (err) {
        error.value = 'Couldn’t save that just now.'
        emit('error', err)
      } finally {
        saving.value = false
      }
    }

    // Back out of the comment step — drops the rating too, so a half-finished
    // answer is never left showing as chosen.
    function cancel() {
      rating.value = null
      comment.value = ''
      error.value = null
    }

    return {
      rating, hoverRating, comment, saving, saved, error,
      settings, labelText, placeholderText, thanksText,
      isStars, stars, commentOpen, starFilled,
      select, submit, cancel,
    }
  },
})
</script>

<template>
  <!-- Nothing until this feature's settings arrive: they decide the shape of the
       control, and without them there is no row for a rating to key to. -->
  <div v-if="settings" class="uf">
    <div v-if="saved" class="uf-thanks">
      <span class="ms fill" style="font-size:16px">favorite</span>
      <span>{{ thanksText }}</span>
    </div>

    <template v-else>
      <div class="uf-row">
        <span class="uf-label">{{ labelText }}</span>

        <!-- Star scale -->
        <div v-if="isStars" class="uf-stars" role="group" :aria-label="labelText" @mouseleave="hoverRating = null">
          <button
            v-for="n in stars"
            :key="n"
            type="button"
            class="uf-star"
            :class="{ on: starFilled(n) }"
            :disabled="saving"
            :aria-pressed="rating === n ? 'true' : 'false'"
            :aria-label="n + (n === 1 ? ' star' : ' stars')"
            @mouseenter="hoverRating = n"
            @focus="hoverRating = n"
            @blur="hoverRating = null"
            @click="select(n)"
          >
            <span class="ms" :class="{ fill: starFilled(n) }" style="font-size:20px">star</span>
          </button>
        </div>

        <!-- Thumbs -->
        <div v-else class="uf-thumbs" role="group" :aria-label="labelText">
          <button
            type="button"
            class="uf-thumb"
            :class="{ on: rating === 1 }"
            :disabled="saving"
            :aria-pressed="rating === 1 ? 'true' : 'false'"
            aria-label="Yes, this was useful"
            @click="select(1)"
          >
            <span class="ms" :class="{ fill: rating === 1 }" style="font-size:18px">thumb_up</span>
          </button>
          <button
            type="button"
            class="uf-thumb"
            :class="{ on: rating === 0 }"
            :disabled="saving"
            :aria-pressed="rating === 0 ? 'true' : 'false'"
            aria-label="No, this wasn't useful"
            @click="select(0)"
          >
            <span class="ms" :class="{ fill: rating === 0 }" style="font-size:18px">thumb_down</span>
          </button>
        </div>
      </div>

      <!-- Comment step (captureComment only) -->
      <div v-if="commentOpen" class="uf-comment">
        <textarea
          v-model="comment"
          class="uf-textarea"
          rows="2"
          maxlength="2000"
          :placeholder="placeholderText"
          :disabled="saving"
        ></textarea>
        <div class="uf-actions">
          <button type="button" class="uf-btn uf-btn--ghost" :disabled="saving" @click="cancel">Cancel</button>
          <button type="button" class="uf-btn" :disabled="saving" @click="submit">
            {{ saving ? 'Sending…' : 'Send' }}
          </button>
        </div>
      </div>

      <!-- Advisory: never blocks whatever the host is doing, just offers a retry -->
      <div v-if="error" class="uf-err">
        {{ error }}
        <button type="button" class="uf-retry" :disabled="saving" @click="submit">Try again</button>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Reads the v3 design tokens where they're in scope (e.g. inside .lvl-eval) and
   falls back to equivalent literals anywhere else in the portal. */
.uf { display: flex; flex-direction: column; gap: 10px; }
/* `.ms` is global (theme.scss) but its FILL variation is only declared inside
   .lvl-eval — repeat it here so filled glyphs work wherever this is dropped. */
.uf .ms.fill { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }

.uf-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.uf-label { font-size: 12.5px; color: var(--v3-text-2, #525c6b); }

.uf-thumbs, .uf-stars { display: inline-flex; align-items: center; gap: 6px; }

.uf-thumb {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 30px; padding: 0;
  border: 1px solid var(--v3-border, #e4e8f2); border-radius: var(--v3-r-sm, 8px);
  background: var(--v3-surface, #fff); color: var(--v3-accent, #2563eb);
  cursor: pointer; transition: background 120ms, border-color 120ms, color 120ms;
}
.uf-thumb:hover:not(:disabled) { border-color: var(--v3-accent, #2563eb); background: var(--v3-accent-soft, rgba(37, 99, 235, 0.08)); }
.uf-thumb.on { border-color: var(--v3-accent, #2563eb); background: var(--v3-accent, #2563eb); color: var(--v3-accent-fg, #fff); }
.uf-thumb.on:hover:not(:disabled) { background: var(--v3-accent, #2563eb); }
.uf-thumb:focus-visible, .uf-star:focus-visible { outline: 2px solid var(--v3-accent, #2563eb); outline-offset: 2px; }
.uf-thumb:disabled, .uf-star:disabled { opacity: .55; cursor: default; }

.uf-star {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; padding: 0; border: 0; border-radius: 6px;
  background: transparent; color: var(--v3-text-3, #97a0af); cursor: pointer;
  transition: color 120ms, transform 120ms;
}
.uf-star.on { color: var(--v3-warn, #b27b12); }
.uf-star:hover:not(:disabled) { transform: scale(1.08); }

.uf-comment { display: flex; flex-direction: column; gap: 8px; max-width: 460px; }
.uf-textarea {
  width: 100%; resize: vertical; font: inherit; font-size: 12.5px; line-height: 1.5;
  padding: 8px 10px; color: var(--v3-text, #1f242d);
  background: var(--v3-surface, #fff);
  border: 1px solid var(--v3-border, #e4e8f2); border-radius: var(--v3-r-sm, 8px);
}
.uf-textarea:focus { outline: none; border-color: var(--v3-accent, #2563eb); }

.uf-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.uf-btn {
  height: 30px; padding: 0 14px; border: 1px solid var(--v3-accent, #2563eb); border-radius: var(--v3-r-sm, 8px);
  background: var(--v3-accent, #2563eb); color: var(--v3-accent-fg, #fff);
  font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.uf-btn:disabled { opacity: .6; cursor: default; }
.uf-btn--ghost { background: transparent; border-color: var(--v3-border, #e4e8f2); color: var(--v3-text-2, #525c6b); }

.uf-thanks { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 650; color: var(--v3-accent, #2563eb); }

.uf-err { display: inline-flex; align-items: center; gap: 8px; font-size: 11.5px; color: var(--v3-text-3, #97a0af); }
.uf-retry { border: 0; background: transparent; padding: 0; font: inherit; font-weight: 600; color: var(--v3-accent, #2563eb); cursor: pointer; text-decoration: underline; }
</style>
